import React, {
    KeyboardEvent,
    SyntheticEvent,
    useEffect,
    useState,
    useRef,
} from 'react';
import cx from 'classnames';
import _isEqual from 'lodash/isEqual';
import { useDeepCompareEffect, useDeepCompareMemo } from 'use-deep-compare';

import { getTailwindColor } from '@trqk/utils';

import { IconEdit } from '..';

import './dropdown-input.css';

const secondary500 = getTailwindColor('secondary-500');

// When doing math to determine how to position the list of options, we need to account for the
// width of borders.
const verticalborderOffset = 4;
const horizontalBorderOffset = 1;

const nullOption = {
    id: null,
    value: null,
    label: '',
};

/**
 * A combination of a dropdown selector with a text input that filters the list of available
 * options, also known as a "combobox" in WAI-ARIA terms. Built off of Example 2 from:
 *      https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
 *
 * Note that this component does not provide its own state machine; it expects the state machine's
 * state to be passed in the `current` prop and the state machine event sender to be passed in the
 * `send` prop.
 *
 * @param props - see {@link BaseDropdownInputProps}
 */
export const BaseDropdownInput = (
    props: BaseDropdownInputProps,
): JSX.Element => {
    const {
        addClasses,
        current,
        defaultId,
        disabled = false,
        disabledOptionIds = new Set(),
        elementId = 'dropdown-input',
        handleChange,
        height = 45,
        highlightIfSelected = true,
        highlightWhenOpen = true,
        maxListDisplayCount = 5,
        resetTimestamp = 0,
        send,
        options,
        optionLists,
        StatusIcon,
        width = 300,
    } = props;
    /* eslint-disable  react/destructuring-assignment */
    const {
        context: { highlightedId, selectedId, didOpen },
    } = current;

    const defaultOption = useDeepCompareMemo((): DropdownInputOption => {
        if (current.context.options && current.context.options.length > 0) {
            return (
                current.context.options.find(
                    ({ id }: DropdownInputOption): boolean => id === defaultId,
                ) ?? nullOption
            );
        }

        if (
            current.context.optionLists &&
            current.context.optionLists.length > 0
        ) {
            return (
                current.context.optionLists
                    .flat(1)
                    .find(
                        ({ id }: DropdownInputOption): boolean =>
                            id === defaultId,
                    ) ?? nullOption
            );
        }

        return nullOption;
    }, [defaultId, current.context.options, current.context.optionLists]);

    const selectedOption =
        useDeepCompareMemo((): DropdownInputOption | null => {
            if (current.context.options) {
                return (
                    current.context.options.find(
                        ({ id }: DropdownInputOption): boolean =>
                            id === selectedId,
                    ) ?? null
                );
            }

            if (current.context.optionLists) {
                return (
                    current.context.optionLists
                        .flat(1)
                        .find(
                            ({ id }: DropdownInputOption): boolean =>
                                id === selectedId,
                        ) ?? null
                );
            }

            return null;
        }, [current.context.options, current.context.optionLists, selectedId]);

    const dropdownWrapperElement = useRef<HTMLDivElement>(null);
    const listElementRef = useRef<HTMLUListElement>(null);
    const inputElementRef = useRef<HTMLInputElement>(null);
    const [listHeightOffset, setListHeightOffset] = useState(height);
    const initialInputValue = selectedOption?.label ?? defaultOption.label;
    const [inputValue, setInputValue] = useState(initialInputValue);
    const isOpen = current.value === 'open';

    let borderStyle = 'border-tertiary-200';

    if (highlightWhenOpen && isOpen) {
        borderStyle = 'border-secondary-500';
    }

    if (
        highlightIfSelected &&
        selectedId !== null &&
        selectedId !== defaultId
    ) {
        borderStyle = 'border-primary-500';
    }

    // Set the default option as the selected option if there is not a selected option. Do this
    // only if the menu has not been opened, to avoid overwriting a change when setting the
    // selectedId to `null` intentionally.
    useEffect(() => {
        if (defaultOption.id !== null && !selectedId && !didOpen) {
            send({
                type: 'SET_DEFAULT',
                value: {
                    optionId: defaultOption.id,
                },
            });
        }
    }, [defaultOption.id, didOpen, selectedId]);

    // If a parent component needs to populate options asynchronously, this will update the current
    // options list.
    useDeepCompareEffect(() => {
        if (
            options !== null &&
            !_isEqual(options, current.context.options) &&
            (options?.length ?? 0) > 0
        ) {
            send({
                type: 'SET_OPTIONS',
                value: options,
            });
        }
    }, [current.context.options, options]);

    // If a parent component needs to populate optionLists asynchronously, this will update the
    // current optionLists
    useDeepCompareEffect(() => {
        if (
            optionLists !== null &&
            !_isEqual(optionLists, current.context.optionLists) &&
            (optionLists?.length ?? 0) > 0
        ) {
            send({
                type: 'SET_OPTION_LISTS',
                value: optionLists,
            });
        }
    }, [current.context.optionLists, optionLists, send]);

    // This ensures that the state machine has the updated `disabledOptionIds` in its
    // current.context
    useDeepCompareEffect(() => {
        if (!_isEqual(disabledOptionIds, current.context.disabledOptionIds)) {
            send({
                type: 'SET_DISABLED_OPTION_IDS',
                value: {
                    disabledOptionIds,
                },
            });
        }
    }, [current.context.disabledOptionIds, disabledOptionIds]);

    // This allows a parent component to reset the input to its default state
    useEffect(() => {
        if (resetTimestamp > 0 && defaultOption.id !== null) {
            send({
                type: 'SET_DEFAULT',
                value: {
                    optionId: defaultOption.id,
                },
            });
        }
    }, [resetTimestamp, defaultOption.id]);

    // maxListDisplayCount is the maximum number of items to display in the list at once. For lists
    // with more than this many options, the <ul> will scroll to show additional items.
    const listHeight =
        height *
        Math.min(
            current?.context?.filteredOptions?.length ?? 0,
            maxListDisplayCount,
        );

    // Close dropdown when user clicks outside of it
    const clickListener = (e: Event): void => {
        const clickedElement = e.target as HTMLElement;

        if (
            dropdownWrapperElement.current &&
            e.target &&
            !dropdownWrapperElement.current.contains(clickedElement) &&
            isOpen
        ) {
            send({ type: 'CANCEL' });
        }
    };

    useEffect(() => {
        document.addEventListener('click', clickListener);

        return (): void => {
            document.removeEventListener('click', clickListener);
        };
    });

    // If the list would go below the viewport when displayed below the input, display it above the
    // input instead
    useEffect(() => {
        if (dropdownWrapperElement.current && listHeightOffset === height) {
            const dropdownRect =
                dropdownWrapperElement.current.getBoundingClientRect();

            const vh = Math.max(
                document.documentElement.clientHeight,
                window.innerHeight || 0,
            );

            if (dropdownRect.bottom + listHeight + verticalborderOffset > vh) {
                setListHeightOffset(-listHeight - verticalborderOffset);
            }
        }
    }, [dropdownWrapperElement, listHeightOffset, height]);

    return (
        <div
            className={cx(
                addClasses,
                'relative flex flex-col justify-start text-sm',
            )}
            ref={dropdownWrapperElement}
            style={{ width, height }}
        >
            <div
                className={cx(
                    'absolute w-full h-full bg-white rounded border',
                    borderStyle,
                    {
                        'shadow-lg': isOpen,
                    },
                )}
            >
                <title id={`${elementId}-label`}>Dropdown Input List</title>
                <div
                    className="flex items-center flex-grow"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={elementId}
                    aria-owns={`${elementId}-options`}
                    aria-haspopup="listbox"
                    style={{
                        height,
                    }}
                >
                    <button
                        type="button"
                        disabled={disabled}
                        className={cx(
                            'flex items-center w-full flex-grow justify-between',
                            {
                                hidden: isOpen,
                            },
                        )}
                        aria-label="Show dropdown text input"
                        onClick={(): void => {
                            send({ type: 'START_EDITING' });

                            setTimeout(() => {
                                if (inputElementRef.current) {
                                    inputElementRef.current.select();
                                }
                            }, 5);
                        }}
                    >
                        <div className="truncate flex-grow ml-1 w-full flex items-center">
                            {StatusIcon && (
                                <StatusIcon
                                    addClasses="flex-none"
                                    fillColor={
                                        selectedOption?.value === null
                                            ? 'tertiary-300'
                                            : 'primary-500'
                                    }
                                />
                            )}
                            <div
                                className={cx(
                                    'ml-1 truncate flex-grow text-left',
                                    {
                                        'text-tertiary-300':
                                            selectedOption?.value === null,
                                        'font-semibold':
                                            selectedOption?.value !== null,
                                    },
                                )}
                            >
                                {selectedOption?.label}
                            </div>
                        </div>
                        <IconEdit
                            fillColor="tertiary-200"
                            width={20}
                            addClasses="mr-2"
                        />
                    </button>
                    <div
                        style={{ height }}
                        className={cx(
                            'w-full flex pl-1 justify-start items-center',
                            {
                                hidden: !isOpen,
                            },
                        )}
                    >
                        {StatusIcon && (
                            <StatusIcon
                                addClasses="flex-none"
                                fillColor="primary-500"
                            />
                        )}
                        <input
                            type="text"
                            aria-autocomplete="list"
                            aria-controls={`${elementId}-options`}
                            aria-labelledby={`${elementId}-label`}
                            aria-activedescendant={`${elementId}.option.${selectedId}`}
                            className="dropdown-input flex-grow w-full ml-1 pr-2 text-black"
                            ref={inputElementRef}
                            onKeyPress={(e: KeyboardEvent): void => {
                                if (
                                    e.key === 'Enter' &&
                                    inputElementRef.current &&
                                    highlightedId !== undefined
                                ) {
                                    send({
                                        type: 'SAVE',
                                        value: {
                                            handleChange,
                                            optionId: highlightedId,
                                        },
                                    });
                                }
                            }}
                            onKeyDown={({ key }: KeyboardEvent): void => {
                                if (key === 'ArrowUp') {
                                    send({
                                        type: 'ARROW_UP',
                                        value: {
                                            listElementRef,
                                            height,
                                        },
                                    });
                                }

                                if (key === 'ArrowDown') {
                                    send({
                                        type: 'ARROW_DOWN',
                                        value: {
                                            listElementRef,
                                            height,
                                            maxListDisplayCount,
                                        },
                                    });
                                }

                                if (['Escape', 'Tab'].includes(key)) {
                                    setInputValue('');
                                    send({ type: 'CANCEL' });
                                }
                            }}
                            onChange={(e: SyntheticEvent): void => {
                                e.preventDefault();
                                const currentValue = (
                                    e.target as HTMLInputElement
                                ).value;

                                setInputValue(currentValue);
                                send({
                                    type: 'INPUT',
                                    value: currentValue,
                                });
                            }}
                            style={{
                                caretColor: secondary500,
                            }}
                            value={inputValue}
                        />
                    </div>
                </div>
                <div
                    className={cx(
                        'absolute flex flex-col flex-grow text-sm rounded border z-10 overflow-hidden',
                        borderStyle,
                        {
                            hidden: !isOpen,
                        },
                    )}
                    style={{
                        top: listHeightOffset,
                        left: -horizontalBorderOffset,
                        width,
                    }}
                >
                    <ul
                        aria-labelledby={`${elementId}-label`}
                        role="listbox"
                        id={`${elementId}-options`}
                        className="w-full flex-grow overflow-y-scroll bg-white"
                        ref={listElementRef}
                        style={{
                            height: listHeight,
                        }}
                    >
                        {current?.context?.filteredOptions &&
                            current.context.filteredOptions.map(
                                (
                                    option: DropdownInputOption,
                                    index: number,
                                ): JSX.Element => {
                                    const optionDisabled =
                                        current.context.disabledOptionIds.has(
                                            option.id,
                                        );

                                    return (
                                        <li
                                            key={`option.${option.id}`}
                                            id={`${elementId}.option.${option.id}`}
                                            className={cx(
                                                'dropdown-input-menu-item flex flex-col justify-center',
                                                {
                                                    highlighted:
                                                        highlightedId ===
                                                        option.id,

                                                    'rounded-t-l': index === 0,
                                                    'rounded-b-l':
                                                        index ===
                                                        current.context
                                                            .filteredOptions
                                                            .length -
                                                            1,
                                                },
                                            )}
                                        >
                                            <button
                                                type="button"
                                                className={cx(
                                                    'w-full text-left px-2 py-1',
                                                    {
                                                        'text-tertiary-400':
                                                            optionDisabled,
                                                    },
                                                )}
                                                disabled={optionDisabled}
                                                onClick={(): void => {
                                                    send({
                                                        type: 'SAVE',
                                                        value: {
                                                            handleChange,
                                                            optionId: option.id,
                                                        },
                                                    });
                                                }}
                                                style={{
                                                    height,
                                                }}
                                            >
                                                <div
                                                    className={cx(
                                                        'clamp-2-lines text-xs',
                                                        {
                                                            'font-semibold':
                                                                selectedId ===
                                                                option.id,
                                                        },
                                                    )}
                                                >
                                                    {option.label}
                                                </div>
                                            </button>
                                        </li>
                                    );
                                },
                            )}
                    </ul>
                </div>
            </div>
        </div>
    );
};
